import { motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import './PremiumTimeline.css';

export type TimelineStep = {
  title: string;
  bullets: string[];
  badge?: string;
  chips?: string[];
};

type PremiumTimelineProps = {
  steps: TimelineStep[];
  closingLine: string;
  closingBody?: string;
  className?: string;
  axisX?: number;
  lineOffsetX?: number;
  cardWidth?: number | string;
  compact?: boolean;
  amplitudeDesktop?: number;
  amplitudeMobile?: number;
  periodDesktop?: number;
  periodMobile?: number;
  tailTopPx?: number;
  tailBottomPx?: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type Point = { x: number; y: number };
type TimelineGeometry = {
  yStart: number;
  yEnd: number;
  stepAnchors: number[];
};

const catmullRomToBezierPath = (points: Point[]) => {
  if (points.length === 0) {
    return '';
  }

  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`;
  }

  let d = `M ${points[0].x} ${points[0].y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[Math.max(index - 1, 0)];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[Math.min(index + 2, points.length - 1)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }

  return d;
};

export default function PremiumTimeline({
  steps,
  closingLine,
  closingBody,
  className,
  axisX,
  lineOffsetX = 56,
  cardWidth = 860,
  compact = false,
  amplitudeDesktop = 18,
  amplitudeMobile = 12,
  periodDesktop = 250,
  periodMobile = 230,
  tailTopPx = 80,
  tailBottomPx = 120,
}: PremiumTimelineProps) {
  const mobileBreakpointPx = 768;
  const mobileAxisX = 24;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const cardRefs = useRef<Array<HTMLElement | null>>([]);
  const markerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const closingLineRef = useRef<HTMLDivElement | null>(null);

  const [pathData, setPathData] = useState('');
  const [pathLength, setPathLength] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [progress, setProgress] = useState(0);
  const [geometry, setGeometry] = useState<TimelineGeometry>({ yStart: 0, yEnd: 1, stepAnchors: [] });
  const [visibleCards, setVisibleCards] = useState<boolean[]>(() => steps.map(() => false));
  const [reducedMotion, setReducedMotion] = useState(false);
  const [endPoint, setEndPoint] = useState<Point>({ x: 0, y: 0 });
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    setVisibleCards((prev) => {
      if (prev.length === steps.length) {
        return prev;
      }

      return steps.map((_, index) => prev[index] ?? false);
    });
  }, [steps]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);

    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(1);
      setVisibleCards(steps.map(() => true));
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleCards((prev) => {
          const next = [...prev];
          let changed = false;

          for (const entry of entries) {
            if (!entry.isIntersecting) {
              continue;
            }

            const index = Number((entry.target as HTMLElement).dataset.timelineIndex);
            if (!Number.isNaN(index) && !next[index]) {
              next[index] = true;
              changed = true;
            }
          }

          return changed ? next : prev;
        });
      },
      { root: null, threshold: 0.4, rootMargin: '0px 0px -8% 0px' },
    );

    cardRefs.current.forEach((node, index) => {
      if (!node) {
        return;
      }

      node.dataset.timelineIndex = String(index);
      observer.observe(node);
    });

    return () => observer.disconnect();
  }, [reducedMotion, steps]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const container = containerRef.current;
    if (!container) {
      return;
    }

    const recomputePath = () => {
      const isMobileViewport = window.matchMedia(`(max-width: ${mobileBreakpointPx}px)`).matches;
      const resolvedAxisX = axisX ?? (isMobileViewport ? mobileAxisX : lineOffsetX);
      const rootRect = container.getBoundingClientRect();
      const anchorPoints = markerRefs.current
        .map((node) => {
          if (!node) {
            return null;
          }

          const rect = node.getBoundingClientRect();
          return {
            x: resolvedAxisX,
            y: rect.top - rootRect.top + rect.height / 2,
          };
        })
        .filter((point): point is { x: number; y: number } => point !== null);

      const height = container.scrollHeight;
      setContainerHeight(height);

      if (anchorPoints.length === 0) {
        setPathData(`M ${resolvedAxisX} 0 L ${resolvedAxisX} ${Math.max(height, 1)}`);
        setGeometry({ yStart: 0, yEnd: Math.max(height, 1), stepAnchors: [] });
        return;
      }

      if (anchorPoints.length === 1) {
        const startY = anchorPoints[0].y - tailTopPx;
        const endY = anchorPoints[0].y + tailBottomPx;
        setPathData(`M ${anchorPoints[0].x} ${startY} L ${anchorPoints[0].x} ${endY}`);
        setGeometry({ yStart: startY, yEnd: endY, stepAnchors: [anchorPoints[0].y] });
        return;
      }

      const stepAnchors = anchorPoints.map((point) => point.y);
      const yStart = stepAnchors[0] - tailTopPx;
      const closingRect = closingLineRef.current?.getBoundingClientRect();
      const closingY = closingRect ? closingRect.top - rootRect.top + closingRect.height / 2 : null;
      const yEndBase = closingY
        ? Math.max(stepAnchors[stepAnchors.length - 1] + tailBottomPx, closingY - 34)
        : stepAnchors[stepAnchors.length - 1] + tailBottomPx;
      const isMobile = window.matchMedia(`(max-width: ${mobileBreakpointPx}px)`).matches;
      const amplitude = isMobile ? amplitudeMobile : amplitudeDesktop;
      const period = isMobile ? periodMobile : periodDesktop;
      const sampleStep = compact ? 12 : 10;
      const phase = 0;
      const omega = (2 * Math.PI) / period;
      const sampledPoints: Point[] = [];

      for (let y = yStart; y <= yEndBase; y += sampleStep) {
        sampledPoints.push({
          x: resolvedAxisX + amplitude * Math.sin(omega * (y - yStart) + phase),
          y,
        });
      }

      if (sampledPoints[sampledPoints.length - 1]?.y !== yEndBase) {
        sampledPoints.push({
          x: resolvedAxisX + amplitude * Math.sin(omega * (yEndBase - yStart) + phase),
          y: yEndBase,
        });
      }

      const nextPathData = catmullRomToBezierPath(sampledPoints);
      const effectiveYEnd = sampledPoints[sampledPoints.length - 1]?.y ?? yEndBase;

      setPathData(nextPathData);
      setGeometry({ yStart, yEnd: effectiveYEnd, stepAnchors });
    };

    recomputePath();

    const resizeObserver = new ResizeObserver(() => recomputePath());
    resizeObserver.observe(container);
    cardRefs.current.forEach((node) => node && resizeObserver.observe(node));

    window.addEventListener('resize', recomputePath);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', recomputePath);
    };
  }, [
    amplitudeDesktop,
    amplitudeMobile,
    axisX,
    compact,
    lineOffsetX,
    periodDesktop,
    periodMobile,
    steps,
    tailBottomPx,
    tailTopPx,
  ]);

  useLayoutEffect(() => {
    if (!pathRef.current || !pathData) {
      return;
    }

    const nextPathLength = pathRef.current.getTotalLength();
    setPathLength(nextPathLength);

    const point = pathRef.current.getPointAtLength(nextPathLength);
    setEndPoint({ x: point.x, y: point.y });
  }, [pathData]);

  useEffect(() => {
    if (isComplete) {
      return;
    }

    if (reducedMotion || progress >= 0.99) {
      setIsComplete(true);
    }
  }, [isComplete, progress, reducedMotion]);

  useEffect(() => {
    if (reducedMotion) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const node = containerRef.current;
    if (!node) {
      return;
    }

    let rafId = 0;

    const updateProgress = () => {
      const rect = node.getBoundingClientRect();
      const scanY = (window.innerHeight || 1) * 0.45;
      const localY = scanY - rect.top;
      const range = Math.max(geometry.yEnd - geometry.yStart, 1);
      const rawProgress = (localY - geometry.yStart) / range;
      setProgress(clamp(rawProgress, 0, 1));
    };

    const requestTick = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateProgress();
      });
    };

    updateProgress();
    window.addEventListener('scroll', requestTick, { passive: true });
    window.addEventListener('resize', requestTick);

    return () => {
      window.removeEventListener('scroll', requestTick);
      window.removeEventListener('resize', requestTick);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [geometry.yEnd, geometry.yStart, reducedMotion]);

  const dashOffset = useMemo(() => {
    if (!pathLength || reducedMotion) {
      return 0;
    }

    return pathLength * (1 - progress);
  }, [pathLength, progress, reducedMotion]);

  const cardMaxWidth = typeof cardWidth === 'number' ? `${cardWidth}px` : cardWidth;
  const fallbackAxisX = axisX ?? mobileAxisX;
  const drawY = geometry.yStart + progress * (geometry.yEnd - geometry.yStart);
  const activationThreshold = 8;
  const highlightWindow = 24;

  return (
    <section
      className={['premium-timeline relative mx-auto w-full max-w-6xl px-4 text-white md:px-6', compact ? 'py-6' : 'py-10 md:py-14', className]
        .filter(Boolean)
        .join(' ')}
      aria-label="Timeline premium"
      style={
        {
          '--ib-accent': '#73d0ff',
          '--ib-accent-2': '#b58dff',
        } as CSSProperties
      }
    >
      <div ref={containerRef} className="relative">
        <svg className="pointer-events-none absolute inset-0 z-0 h-full w-full overflow-visible" aria-hidden="true">
          <defs>
            <filter id="timeline-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="0" stdDeviation="2.6" floodColor="rgba(255,255,255,0.34)" />
            </filter>
          </defs>
          <path
            d={pathData || `M ${fallbackAxisX} 0 L ${fallbackAxisX} ${Math.max(containerHeight, 1)}`}
            fill="none"
            stroke="var(--premium-timeline-path-glow, rgba(255,255,255,0.2))"
            strokeWidth={6}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: 'blur(6px)' }}
          />
          <path
            ref={pathRef}
            d={pathData || `M ${fallbackAxisX} 0 L ${fallbackAxisX} ${Math.max(containerHeight, 1)}`}
            fill="none"
            stroke="var(--premium-timeline-path-active, rgba(255,255,255,0.78))"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#timeline-glow)"
            style={{
              strokeDasharray: pathLength || undefined,
              strokeDashoffset: dashOffset,
              transition: reducedMotion ? 'none' : 'stroke-dashoffset 0.16s linear',
            }}
          />
          <circle
            cx={endPoint.x}
            cy={endPoint.y}
            r={5}
            fill="var(--premium-timeline-path-dot, rgba(115,208,255,0.95))"
            style={{
              opacity: isComplete || reducedMotion ? 0.95 : 0.45,
              transition: 'opacity 320ms ease, transform 320ms ease, box-shadow 320ms ease',
              transformOrigin: `${endPoint.x}px ${endPoint.y}px`,
              transform: isComplete && !reducedMotion ? 'scale(1.08)' : 'scale(1)',
            }}
            className={isComplete && !reducedMotion ? 'animate-[pulse_1.9s_ease-in-out_infinite]' : ''}
          />
        </svg>

        <ol className="relative z-10 space-y-[1.1rem] md:space-y-7" role="list">
          {steps.map((step, index) => {
            const visible = reducedMotion || visibleCards[index];
            const anchorY = geometry.stepAnchors[index] ?? Number.POSITIVE_INFINITY;
            const isActive = reducedMotion || drawY >= anchorY - activationThreshold;
            const isCurrent = !reducedMotion && Math.abs(drawY - anchorY) <= highlightWindow;
            return (
              <li key={`${step.title}-${index}`} className="relative block md:grid md:grid-cols-[72px_minmax(0,1fr)] md:items-start md:gap-6">
                <div className="absolute left-2 top-2 z-20 flex h-full w-8 items-start justify-center md:relative md:left-0 md:top-0 md:w-[72px] md:pt-2">
                  <div
                    ref={(node) => {
                      markerRefs.current[index] = node;
                    }}
                    className={[
                      'relative z-20 flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-semibold transition-all duration-200 md:h-10 md:w-10 md:text-sm',
                      isActive
                        ? 'border border-[var(--ib-accent)]/80 bg-slate-900/90 text-white ring-2 ring-[var(--ib-accent)]/80 shadow-[0_0_0_6px_rgba(115,208,255,0.16),0_0_26px_rgba(115,208,255,0.38)]'
                        : 'border border-white/35 bg-slate-900/80 text-white shadow-[0_0_0_4px_rgba(255,255,255,0.06)]',
                      isCurrent
                        ? 'ring-[var(--ib-accent-2)]/80 shadow-[0_0_0_8px_rgba(181,141,255,0.24),0_0_32px_rgba(115,208,255,0.45)]'
                        : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-hidden="true"
                  >
                    {index + 1}
                  </div>
                </div>

                <motion.article
                  ref={(node) => {
                    cardRefs.current[index] = node;
                  }}
                  initial={false}
                  animate={
                    visible
                      ? { opacity: 1, y: 0, filter: 'blur(0px)' }
                      : { opacity: 0, y: 10, filter: 'blur(2px)' }
                  }
                  transition={
                    reducedMotion ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: index * 0.04 }
                  }
                  className="premium-timeline-card relative z-10 ml-12 w-[calc(100%-3rem)] p-4 md:ml-0 md:w-full md:p-7"
                  style={{ maxWidth: cardMaxWidth }}
                >
                  {step.badge ? (
                    <span className="premium-timeline-badge mb-3 inline-flex px-[9px] py-[3px] text-[9px] font-medium tracking-[0.18em]">
                      {step.badge}
                    </span>
                  ) : null}
                  <h3 className="premium-timeline-title text-xl font-semibold leading-tight md:text-2xl">{step.title}</h3>
                  <ul className="premium-timeline-bullets mt-3 space-y-2 text-base leading-relaxed md:text-lg">
                    {step.bullets.map((bullet) => (
                      <li key={`${step.title}-${bullet}`} className="list-none">
                        • {bullet}
                      </li>
                    ))}
                  </ul>
                  {step.chips?.length ? (
                    <ul className="mt-4 flex flex-wrap gap-2" aria-label="Step tags">
                      {step.chips.map((chip) => (
                        <li
                          key={`${step.title}-${chip}`}
                          className="premium-timeline-chip inline-flex px-2 py-[3px] text-[8px] font-medium uppercase tracking-[0.06em]"
                        >
                          {chip}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </motion.article>
              </li>
            );
          })}
        </ol>

        <motion.div
          ref={closingLineRef}
          data-completed={isComplete ? 'true' : 'false'}
          initial={false}
          animate={
            reducedMotion || !isComplete
              ? {
                opacity: 1,
                scale: 1,
                borderColor: 'rgba(255,255,255,0.2)',
                boxShadow: '0 14px 34px rgba(24, 18, 47, 0.24)',
              }
              : {
                opacity: [0.94, 1],
                scale: [1, 1.02, 1],
                borderColor: ['rgba(255,255,255,0.2)', 'rgba(170, 228, 255, 0.62)', 'rgba(176, 232, 255, 0.72)'],
                boxShadow: [
                  '0 14px 34px rgba(24, 18, 47, 0.24)',
                  '0 0 0 1px rgba(184, 236, 255, 0.4), 0 0 28px rgba(126, 211, 255, 0.32), inset 0 0 40px rgba(173, 137, 255, 0.14)',
                  '0 0 0 1px rgba(184, 236, 255, 0.32), 0 0 24px rgba(126, 211, 255, 0.26), inset 0 0 34px rgba(173, 137, 255, 0.12)',
                ],
              }
          }
          transition={reducedMotion ? { duration: 0 } : { duration: 0.7, ease: 'easeOut', times: [0, 0.62, 1] }}
          className={[
            'timeline-closing-card premium-timeline-closing relative z-10 ml-12 mt-10 w-[calc(100%-3rem)] max-w-[860px] px-4 py-4 text-left text-base leading-relaxed md:ml-[96px] md:mt-14 md:w-auto md:px-7 md:py-5 md:text-lg',
            isComplete && !reducedMotion ? 'completed text-white' : '',
          ].filter(Boolean).join(' ')}
          style={{
            backgroundImage: 'radial-gradient(circle at 18% 48%, rgba(152,214,255,0.14), transparent 55%)',
          }}
        >
          <p className="font-semibold leading-snug">{closingLine}</p>
          {closingBody ? <p className="mt-2">{closingBody}</p> : null}
        </motion.div>
      </div>
    </section>
  );
}
